import { IonButton, IonButtons, IonContent, IonIcon, IonImg, IonLabel, IonPage, useIonAlert } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useTheme } from "react-jss";
import FormField from "../../../components/inputs/input";
import { RequestAddContainerStyle } from "./containerStyle.jss";
import truck from "../../../assets/image/simplyTruck.png";
import { ISelectorOption, Selector } from "../../../components/selector/selector";
import { UnitTransportTypes } from "../../../constants/unitTransportTypes";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { IAddRequestForm, resetAddRequestForm, updateAddRequestProperty } from "../../../redux/reducers/request/create/form";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { compileNameOfProperty } from "../../../utils/utils";
import { isValidContainer, isValidTruck, notExpecialsCaracters } from "../../../validation/validation";
import { DefaultValues } from "../../../constants/defaultValues";
import { MaxLengths } from "../../../constants/maxLengths";
import Header from "../../../components/header/header";
import { arrowBackOutline } from "ionicons/icons";
import { useHistory } from "react-router";
import { Footer } from "../../../components/footer/footer";
import { Button, ButtonWithInverse } from "../../../components/button/button";
import { addNewRequest } from "../../../redux/actions/request/request";
import { AppRoutes } from "../../../constants/appRoutes";

const RequestAddContainer: React.FC = () => {
    const theme = useTheme();
    const style = RequestAddContainerStyle({ theme });
    const { t } = useTranslation("common");
    const dispatch = useAppDispatch();
    const { form } = useAppSelector((state) => state.addRequestForm);
    const history = useHistory();
    const onUpdateHeaderProperty = (name: string, value: any) => {
        dispatch(updateAddRequestProperty({ name, value }));
    };

    const onChangeSelector = (ev: any) => {
        onUpdateHeaderProperty(compileNameOfProperty<IAddRequestForm>("transportTypeId"), Number(ev.detail.value));

        onUpdateHeaderProperty(compileNameOfProperty<IAddRequestForm>("transportUnitNumber"), DefaultValues.DEFAULT_TEXT);
    };
    const onChangeInput = (ev: any) => {
        onUpdateHeaderProperty(compileNameOfProperty<IAddRequestForm>("transportUnitNumber"), ev.target.value);
    };

    const options: ISelectorOption[] = [
        {
            description: t("GLOBAL.TRUCK"),
            id: UnitTransportTypes.TRUCK,
            key: UnitTransportTypes.TRUCK.toString(),
        },
        {
            description: t("GLOBAL.CONTAINER"),
            id: UnitTransportTypes.CONTAINER,
            key: UnitTransportTypes.CONTAINER.toString(),
        },
    ];

    const labelOfTransportType = useMemo(() => {
        if (form.transportTypeId === UnitTransportTypes.TRUCK) {
            return t("PAGE.NEW_REQUEST.MESSAGE_TRUCK");
        } else if (form.transportTypeId === UnitTransportTypes.CONTAINER) {
            return t("PAGE.NEW_REQUEST.MESSAGE_CONTAINER");
        }
        return "";
    }, [form]);

    const { control } = useForm<IAddRequestForm>({ mode: "onChange" });

    const errorMessage = useMemo(() => {
        if (form.transportUnitNumber) {
            if (!notExpecialsCaracters(form.transportUnitNumber)) {
                return { type: "validate", message: t("VALIDATION.NOT_EXPECIALS_CARACTER") };
            }

            if (form.transportTypeId === UnitTransportTypes.TRUCK && !isValidTruck(form.transportUnitNumber)) {
                return { type: "validate", message: t("VALIDATION.TRUCK") };
            }

            if (form.transportTypeId === UnitTransportTypes.CONTAINER && !isValidContainer(form.transportUnitNumber)) {
                return { type: "validate", message: t("VALIDATION.CONTAINER") };
            }
        }

        return undefined;
    }, [form]);

    const [presentAlert] = useIonAlert();

    const onCancelPopUp = () => {
        history.goBack();
        dispatch(resetAddRequestForm());
    };

    const onSavePopUp = () => {
        dispatch(addNewRequest()).then((response) => {
            if (response.success) {
                onCancelPopUp();
            } else {
                presentAlert({
                    header: t("GLOBAL.WARNING"),
                    backdropDismiss: false,
                    message: response.withMessage.response.data.detail,
                    buttons: [t("GLOBAL.WARNING_ACCEPT")],
                });
            }
        });
    };

    const disabledButton = useMemo(() => {
        if (!form.transportUnitNumber || !form.transportTypeId || !notExpecialsCaracters(form.transportUnitNumber)) {
            return true;
        }

        if (form.transportTypeId === UnitTransportTypes.TRUCK && !isValidTruck(form.transportUnitNumber)) {
            return true;
        }

        if (form.transportTypeId === UnitTransportTypes.CONTAINER && !isValidContainer(form.transportUnitNumber)) {
            return true;
        }

        return false;
    }, [form]);

    return (
        <IonPage>
            <Header
                element={
                    <IonButtons slot="start">
                        <IonButton
                            routerDirection="back"
                            routerLink={AppRoutes.REQUEST}
                            onClick={() => onCancelPopUp()}
                        >
                            <IonIcon
                                slot="icon-only"
                                icon={arrowBackOutline}
                                size={"large"}
                                className={style.icon}
                            />
                        </IonButton>
                    </IonButtons>
                }
                title={t("PAGE.REQUEST.CREATE")}
                showLogout={false}
            />

            <IonContent className="ion-padding">
                <div className={style.container}>
                    <IonLabel> {t("PAGE.NEW_REQUEST.MESSAGE")} </IonLabel>
                    <div className={style.row}>
                        <IonImg
                            className={style.icon}
                            src={truck}
                        />
                        <Selector
                            label={t("PAGE.NEW_REQUEST.LABEL")}
                            options={options}
                            onChange={onChangeSelector}
                            selectedKey={String(form.transportTypeId)}
                        ></Selector>
                    </div>

                    {form.transportTypeId !== 0 && (
                        <>
                            <IonLabel> {labelOfTransportType} </IonLabel>
                            <div className={style.row}>
                                <FormField
                                    value={form.transportUnitNumber}
                                    label={t("GLOBAL.TRANSPORT_UNIT_NUMBER")}
                                    onChange={onChangeInput}
                                    control={control}
                                    type={"text"}
                                    name={compileNameOfProperty<IAddRequestForm>("transportUnitNumber")}
                                    error={errorMessage}
                                    maxLength={MaxLengths.TRANSPORT_UNIT_NUMBER}
                                ></FormField>
                            </div>
                        </>
                    )}
                </div>
            </IonContent>

            <Footer
                element={
                    <>
                        <ButtonWithInverse
                            title={t("GLOBAL.CLOSE")}
                            onClick={onCancelPopUp}
                        ></ButtonWithInverse>

                        <Button
                            onClick={onSavePopUp}
                            color={"secondary"}
                            title={t("GLOBAL.ADD")}
                            disabled={disabledButton}
                        />
                    </>
                }
            ></Footer>
        </IonPage>
    );
};

export default RequestAddContainer;
